/**
 * Import Template Builder
 * Generates a multi-tab Excel workbook for bulk data import.
 * Uses ExcelJS to create themed worksheets with FK dropdown and Lookup validation.
 */
import ExcelJS from 'exceljs';
import { appCfgApi, dynamicApi } from '../services/api';

// ===== Table Configuration =====
// Maps friendly tab names to database table names
export const TABLE_CONFIG = [
  { tabName: '1 - Source',           dbTable: 'Source' },
  { tabName: '2 - Drawings',        dbTable: 'Drawing' },
  { tabName: '3 - Drawing Log',     dbTable: 'Drawing_Log' },
  { tabName: '4 - Panels',          dbTable: 'Panels' },
  { tabName: '5 - Tags',            dbTable: 'Tags' },
  { tabName: '6 - SLN IP Address',  dbTable: 'IP_Address_SLN' },
  { tabName: '7 - DLN IP Address',  dbTable: 'IP_Address_DLN' },
  { tabName: '8 - Network ENET',     dbTable: 'Networks_ENET' },
  { tabName: '9 - Network Serial',  dbTable: 'Networks_Serial' },
  { tabName: '10 - Pwr Dist',        dbTable: 'Pwr_Dist' }
];

// ===== Static Enum Values (Fallback) =====
// Used only when field has no lookup_Category assigned in metadata
const STATIC_ENUMS = {
  // Submittal / approval status fields
  'Submittal_Status': ['Submitted', 'Approved', 'Rejected', 'N/A'],
  'Status': ['Active', 'Inactive', 'Pending', 'Complete'],
  'OAT_Complete': ['Yes', 'No', 'N/A'],
  'Supply_By': ['Owner', 'Contractor', 'Sub-Contractor', 'Vendor', 'N/A'],
  'Dock_Status': ['Received', 'Not Received', 'Partial', 'N/A'],
  'IO_Type': ['DI', 'DO', 'AI', 'AO', 'N/A'],
  'Protocol': ['BACnet IP', 'BACnet MSTP', 'Modbus TCP', 'Modbus RTU', 'LON', 'N/A'],
  'Baud_Rate': ['9600', '19200', '38400', '57600', '76800', '115200'],
  'Parity': ['None', 'Even', 'Odd'],
  'Data_Bits': ['7', '8'],
  'Stop_Bits': ['1', '2']
};

// ===== Theme Colors =====
const THEME = {
  headerFill: 'FF1A5568',      // WM teal/petrol blue
  headerFont: 'FFFFFFFF',      // White
  headerBorder: 'FF134452',    // Darker teal
  instructionTitle: 'FF1A5568',
  instructionBg: 'FFF0F9FF',
  altRowBg: 'FFF8FAFB',
  listsHeaderFill: 'FF64748B', // Slate for Lists sheet
  listsHeaderFont: 'FFFFFFFF'
};

// Fields to exclude from import templates (auto-generated, calculated, or system fields)
// Note: ID is intentionally included as the first column — blank = new record, populated = update target
const EXCLUDED_FIELDS = [];

// Fields that should NOT get dropdown validation (free-text even though they have FK/lookup metadata)
const NO_DROPDOWN = ['Source', 'Parent', 'Drawing', 'Drawing_Name', 'Project_Number', 'Panel', 'Panel_Fed', 'Tag_Fed', 'Tag_ID', 'DTC'];

// Numeric range validation — field name -> { min, max }
// (Currently unused — Port and CNX_Sequence moved to lookup categories)
const NUMERIC_RANGES = {};

/**
 * Main entry point: generates and downloads the import template workbook.
 *
 * @param {Object} options
 * @param {Array} options.selectedTables - Array of TABLE_CONFIG entries
 * @param {Function} options.onProgress - Called with { step, total, message }
 * @param {Object} options.notify - Notification context { success, error, info }
 */
export async function generateImportTemplate({ selectedTables, onProgress, notify }) {
  const totalSteps = 4;
  let currentStep = 0;

  const progress = (message) => {
    currentStep++;
    onProgress?.({ step: currentStep, total: totalSteps, message });
  };

  try {
    // Step 1: Fetch field metadata for all selected tables
    progress('Fetching field metadata...');
    const metadataMap = {};
    const metadataResults = await Promise.allSettled(
      selectedTables.map(async (t) => {
        const res = await appCfgApi.getFieldMetadata(t.dbTable);
        return { dbTable: t.dbTable, data: res.data };
      })
    );

    const tablesWithWarnings = [];
    for (const result of metadataResults) {
      if (result.status === 'fulfilled') {
        metadataMap[result.value.dbTable] = result.value.data;
      } else {
        // Table metadata fetch failed — we'll still include it but flag it
        tablesWithWarnings.push(result.reason?.message || 'Unknown table');
      }
    }

    // Step 2: Identify FK tables and fetch lookup data + lookup categories
    progress('Fetching lookup data for dropdowns...');
    const fkTableSet = new Set();
    const fkDisplayFieldMap = {}; // fkTable -> fkDisplayField

    for (const [, fields] of Object.entries(metadataMap)) {
      for (const field of fields) {
        if (field.fK_Table && field.fK_Display_Field) {
          fkTableSet.add(field.fK_Table);
          fkDisplayFieldMap[field.fK_Table] = field.fK_Display_Field;
        }
      }
    }

    // Fetch FK lookup data and Lookup Values in parallel
    const fkDataMap = {}; // fkTable -> array of display values
    let lookupMap = {}; // category -> array of values

    const [, lookupsResult] = await Promise.allSettled([
      // FK lookups
      (async () => {
        if (fkTableSet.size > 0) {
          const fkResults = await Promise.allSettled(
            [...fkTableSet].map(async (fkTable) => {
              const res = await dynamicApi.getAll(fkTable);
              const displayField = fkDisplayFieldMap[fkTable];
              const values = res.data
                .map(row => {
                  // Dynamic API returns camelCased keys — do case-insensitive match
                  if (row[displayField] !== undefined) return row[displayField];
                  const key = Object.keys(row).find(k => k.toLowerCase() === displayField.toLowerCase());
                  return key ? row[key] : null;
                })
                .filter(Boolean)
                .sort((a, b) => String(a).localeCompare(String(b)));
              return { fkTable, values };
            })
          );
          for (const result of fkResults) {
            if (result.status === 'fulfilled') {
              fkDataMap[result.value.fkTable] = result.value.values;
            }
          }
        }
      })(),
      // Lookup values (from Lookup Values system)
      appCfgApi.getAllLookups()
    ]);

    if (lookupsResult.status === 'fulfilled') {
      lookupMap = lookupsResult.value.data; // { category: [values] }
    }

    // Step 3: Build workbook
    progress('Building Excel workbook...');
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'PMD Import Template Generator';
    workbook.created = new Date();

    // --- Instructions Tab ---
    const instructionsSheet = buildInstructionsSheet(workbook, selectedTables);

    // --- Lists Tab (hidden) — FK values + Lookup category values ---
    const listsSheet = buildListsSheet(workbook, fkDataMap, lookupMap);

    // --- Data Tabs ---
    const dataSheets = [];
    for (const tableConfig of selectedTables) {
      const fields = metadataMap[tableConfig.dbTable];
      if (!fields) continue; // Skip if metadata fetch failed

      dataSheets.push(buildDataSheet(workbook, tableConfig, fields, fkDataMap, fkDisplayFieldMap, listsSheet, lookupMap));
    }

    // Step 4: Download
    progress('Generating file...');
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const filename = `PMD_Import_Template_${timestamp}.xlsx`;

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    if (tablesWithWarnings.length > 0) {
      notify?.info(`Template generated. ${tablesWithWarnings.length} table(s) had missing metadata.`);
    } else {
      notify?.success(`Template downloaded: ${filename}`);
    }

    return { success: true, filename, warnings: tablesWithWarnings };
  } catch (err) {
    console.error('Import template generation failed:', err);
    notify?.error(`Template generation failed: ${err.message}`);
    return { success: false, error: err.message };
  }
}

// ===== Sheet Builders =====

// ===== Tab-Specific Instructions =====
const TAB_INSTRUCTIONS = {
  'Source': [
    'Get the information to populate fields from the SharePoint Project List. Project Number and Project Description can be taken directly from the metadata of the project folder. Project Scope shall be "Contract" unless otherwise directed.'
  ],
  'Drawing': [
    'For each drawing in the project directory, verify whether the drawing already exists in the PMD. Go to the Drawings module and enter the drawing name in the search field. Make sure to only enter the drawing name (e.g. FB-J-8AMUA002) and not any appended information like "S19-R3".',
    'If the drawing already exists in the PMD then do not enter it into this tab. Only new drawings get added here.'
  ],
  'Drawing_Log': [
    'Whether or not the drawing is new, the revision information needs to be entered into this tab. Only enter new revisions.',
    'When checking for the existence of a drawing, take note of the latest revision number and compare to the project drawing. It is possible for drawings to have the same revision number but different revision dates — those differences need to be captured here.'
  ],
  'Panels': [
    'Fill out as much information as possible. The exact card configuration may not be known but if the Panel is a PLC with a 7-slot chassis then mark unused slots as N/A. If the Panel Type is a VFD then mark all slots as N/A.',
    'Drawing Column: If the panel is a PLC/RIO or Network switch, enter the network drawing name. If the panel is a VFD, enter the P&ID that the VFD appears on.',
    'Default data has been added for columns prefixed with "CS_*". That is the typical configuration for most panels. Make sure to copy this data down to any new rows.'
  ],
  'Tags': [
    'Refer to tagging spec for details on how tags should be structured.',
    'Description: Use proper text — formatted as "Building", "System", "Equipment", "Instrument Description". E.g. "FAB FB Cleanroom Make-Up Pre-Cool Temp". Find a similar instrument in the PMD and format accordingly.',
    'Edit: If the tag is new choose "Add". If modifying or deleting an existing tag, copy the data from the PMD and highlight changed fields in YELLOW.',
    'Parent/Panel/IO Type/Drop/Slot/Channel is entered based on instrument location and IO availability. Verify IO availability before assigning to an existing panel.',
    'ISA Type can be found on the drawing.',
    'Address is typically "N/A" unless the tag is a networked VFD or wired to a VFD.',
    'Min/Max/EU may not be known until the instrument is purchased.',
    'Wire Type/Power Req/PTAP Req may not be known until the instrument is purchased.',
    'DTAG_Req is typically "NEED" unless modifying or deleting a tag.',
    'Building/Location is found on the location drawings.',
    'DTC/DTC_Loc is typically "N/A" unless there really is a DTC cabinet.'
  ],
  'IP_Address_SLN': [
    'Check the SLN IP Address module in the PMD before entering any data. Find blocks of IP addresses and look for patterns.'
  ],
  'IP_Address_DLN': [
    'Same as SLN — DLN addresses have specific groupings:',
    'PLC DLN cards are typically *.1/*.2.',
    'Panel Type R panel status monitoring RIOs are typically *.8 and *.9.',
    'RIOs are addressed by adding 10 to the last 2 digits of the panel name. E.g. FB1PCTRLS101 = *.11',
    'Vendor panels like chillers or chemical monitoring skids start at *.60.',
    'VFDs start at *.101 with redundant connections being +50. E.g. Primary = *.101, Redundant = *.151',
    'Network switches for RIOs are +200 of their RIO address. E.g. FB1PCTRLS101 = *.11, FB1PNETWS101 = *.211'
  ],
  'Networks_ENET': [
    'If adding new network switches, the entire port structure of the switch must be entered. For example, if adding a 24-port switch then 24 rows are needed. Use existing PMD data for examples.'
  ],
  'Networks_Serial': [
    'Same as ENET — enter the full port/connection structure for new serial network devices.'
  ],
  'Pwr_Dist': []
};

function buildInstructionsSheet(workbook, selectedTables) {
  const ws = workbook.addWorksheet('Instructions', {
    properties: { tabColor: { argb: 'FF1A5568' } }
  });

  ws.columns = [
    { width: 5 },
    { width: 25 },
    { width: 100 }
  ];

  const sectionFont = { name: 'Calibri', size: 14, bold: true, color: { argb: THEME.instructionTitle } };
  const labelFont = { name: 'Calibri', size: 11, bold: true };
  const bodyFont = { name: 'Calibri', size: 11 };
  const subFont = { name: 'Calibri', size: 11, color: { argb: 'FF475569' } };

  // Title
  ws.mergeCells('B2:C2');
  const titleCell = ws.getCell('B2');
  titleCell.value = 'PMD Bulk Import Template';
  titleCell.font = { name: 'Calibri', size: 18, bold: true, color: { argb: THEME.instructionTitle } };

  // Date
  ws.getCell('B3').value = `Generated: ${new Date().toLocaleDateString()}`;
  ws.getCell('B3').font = { name: 'Calibri', size: 11, color: { argb: 'FF64748B' } };

  let row = 5;

  // === General Information ===
  ws.mergeCells(`B${row}:C${row}`);
  ws.getCell(`B${row}`).value = 'General Information';
  ws.getCell(`B${row}`).font = sectionFont;
  row += 2;

  const generalInfo = [
    ['Layout', 'Each tab in this document is numbered. This is the execution order in which data must be imported into the PMD.'],
    ['Header Row', 'Row 1 contains column headers — do NOT modify header names.'],
    ['Data Entry', 'Enter data starting from Row 2.'],
    ['List Validation', 'Columns with dropdown arrows have validation — select from the list. If new items need to be added to a dropdown list, add the data to the appropriate table in the PMD first, then regenerate this template.'],
    ['Date Fields', 'The format for date fields is MM/DD/YYYY. Do not enter time for any date field.'],
    ['Revision User', 'Revision User fields should match the display name entered in the PMD. After logging in, the display name is located in the lower left corner of the Home screen.'],
    ['ID Column', 'The first column on every tab is the ID. Leave blank for new records. Populate with an existing record\'s ID to update that record. Do not guess or make up IDs.'],
    ['File Format', 'Save as .xlsx before importing.'],
    ['Bulk Updates', 'When bulk updating existing data, download a fresh copy of the Power Query and copy data from the Power Query and "paste values" only into the PMD Import Template. Copying and pasting directly into the PMD Import Template could break the conditional formatting and data validation.']
  ];

  for (const [label, text] of generalInfo) {
    ws.getCell(`B${row}`).value = label;
    ws.getCell(`B${row}`).font = labelFont;
    ws.getCell(`C${row}`).value = text;
    ws.getCell(`C${row}`).font = bodyFont;
    ws.getCell(`C${row}`).alignment = { wrapText: true, vertical: 'top' };
    row++;
  }

  // === Tab Specific Instructions ===
  row += 1;
  ws.mergeCells(`B${row}:C${row}`);
  ws.getCell(`B${row}`).value = 'Tab Specific Instructions';
  ws.getCell(`B${row}`).font = sectionFont;
  row += 2;

  for (const t of selectedTables) {
    const instructions = TAB_INSTRUCTIONS[t.dbTable] || [];
    if (instructions.length === 0) continue;

    // Tab name header
    ws.getCell(`B${row}`).value = t.tabName;
    ws.getCell(`B${row}`).font = labelFont;
    ws.getCell(`C${row}`).value = instructions[0];
    ws.getCell(`C${row}`).font = bodyFont;
    ws.getCell(`C${row}`).alignment = { wrapText: true, vertical: 'top' };
    row++;

    // Additional instructions as sub-items
    for (let i = 1; i < instructions.length; i++) {
      ws.getCell(`C${row}`).value = instructions[i];
      ws.getCell(`C${row}`).font = subFont;
      ws.getCell(`C${row}`).alignment = { wrapText: true, vertical: 'top' };
      row++;
    }
    row++; // spacer between tabs
  }

  return ws;
}

function buildListsSheet(workbook, fkDataMap, lookupMap = {}) {
  const ws = workbook.addWorksheet('Lists', {
    properties: { tabColor: { argb: 'FF64748B' } },
    state: 'hidden'
  });

  let colIndex = 1;

  // FK table columns
  const fkTables = Object.keys(fkDataMap).sort();
  for (const fkTable of fkTables) {
    const values = fkDataMap[fkTable];
    ws.getColumn(colIndex).width = 25;

    const headerCell = ws.getCell(1, colIndex);
    headerCell.value = fkTable;
    headerCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: THEME.listsHeaderFont } };
    headerCell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: THEME.listsHeaderFill }
    };

    for (let i = 0; i < values.length; i++) {
      ws.getCell(i + 2, colIndex).value = values[i];
    }
    colIndex++;
  }

  // Lookup category columns
  const lookupCategories = Object.keys(lookupMap).sort();
  for (const category of lookupCategories) {
    const values = lookupMap[category];
    if (!values || values.length === 0) continue;

    ws.getColumn(colIndex).width = 25;

    const headerCell = ws.getCell(1, colIndex);
    headerCell.value = `LU_${category}`;
    headerCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: THEME.listsHeaderFont } };
    headerCell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: THEME.listsHeaderFill }
    };

    for (let i = 0; i < values.length; i++) {
      ws.getCell(i + 2, colIndex).value = String(values[i]);
    }
    colIndex++;
  }

  // Store column mapping for reference (FK tables + lookup categories)
  ws._fkColumnMap = {};
  fkTables.forEach((table, idx) => {
    ws._fkColumnMap[table] = {
      colLetter: getColumnLetter(idx + 1),
      rowCount: fkDataMap[table].length
    };
  });

  ws._lookupColumnMap = {};
  let luColStart = fkTables.length + 1;
  for (const category of lookupCategories) {
    const values = lookupMap[category];
    if (!values || values.length === 0) continue;
    ws._lookupColumnMap[category] = {
      colLetter: getColumnLetter(luColStart),
      rowCount: values.length
    };
    luColStart++;
  }

  return ws;
}

function buildDataSheet(workbook, tableConfig, fields, fkDataMap, fkDisplayFieldMap, listsSheet, lookupMap = {}) {
  const ws = workbook.addWorksheet(tableConfig.tabName, {
    properties: { tabColor: { argb: 'FF1A5568' } }
  });

  // Filter out excluded fields, ensure ID is first
  // API returns camelCased snake_case: field_Name, display_Label, fK_Table, etc.
  const nonIdFields = fields.filter(f =>
    !EXCLUDED_FIELDS.includes(f.field_Name) && f.field_Name !== 'ID'
  );
  const idField = { field_Name: 'ID', display_Label: 'ID', uI_Type: 'number', fK_Table: null, lookup_Category: null };
  const importFields = [idField, ...nonIdFields];

  // Set up columns with autosized widths
  const columns = importFields.map((f, idx) => {
    const fieldName = f.field_Name;
    const label = f.display_Label || fieldName.replace(/_/g, ' ');

    // Autosize: start with header length, expand for known dropdown values
    let maxLen = label.length + 2;

    // Check FK values
    if (f.fK_Table && fkDataMap[f.fK_Table]) {
      const longest = fkDataMap[f.fK_Table].reduce((max, v) => Math.max(max, String(v).length), 0);
      maxLen = Math.max(maxLen, longest + 2);
    }
    // Check lookup values
    if (f.lookup_Category && lookupMap[f.lookup_Category]) {
      const longest = lookupMap[f.lookup_Category].reduce((max, v) => Math.max(max, String(v).length), 0);
      maxLen = Math.max(maxLen, longest + 2);
    }

    // Clamp between 10 and 45
    const width = Math.max(10, Math.min(maxLen, 45));

    return {
      header: label,
      key: fieldName,
      width
    };
  });

  ws.columns = columns;

  // Style header row
  const headerRow = ws.getRow(1);
  headerRow.height = 28;
  headerRow.eachCell((cell, colNumber) => {
    const isIdCol = colNumber === 1;
    cell.font = {
      name: 'Calibri', size: 11, bold: true,
      color: { argb: isIdCol ? 'FF94A3B8' : THEME.headerFont }
    };
    cell.fill = {
      type: 'pattern', pattern: 'solid',
      fgColor: { argb: isIdCol ? 'FF334155' : THEME.headerFill }
    };
    cell.border = {
      bottom: { style: 'medium', color: { argb: THEME.headerBorder } }
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  // Freeze header row
  ws.views = [{ state: 'frozen', ySplit: 1 }];

  // Apply data validation for rows 2–1001
  const DATA_ROWS = 1000;

  importFields.forEach((f, colIdx) => {
    const fieldName = f.field_Name;
    const fkTable = f.fK_Table;
    const lookupCategory = f.lookup_Category;
    const colNumber = colIdx + 1;

    // FK dropdown validation — points to Lists sheet
    if (fkTable && !NO_DROPDOWN.includes(fieldName) && listsSheet._fkColumnMap?.[fkTable]) {
      const mapping = listsSheet._fkColumnMap[fkTable];
      const formula = `Lists!$${mapping.colLetter}$2:$${mapping.colLetter}$${mapping.rowCount + 1}`;

      for (let row = 2; row <= DATA_ROWS + 1; row++) {
        ws.getCell(row, colNumber).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [formula],
          showErrorMessage: true,
          errorTitle: 'Invalid Value',
          error: `Please select a value from the ${fkTable} list.`
        };
      }
    }
    // Lookup category validation — points to Lists sheet (LU_ columns)
    else if (lookupCategory && !NO_DROPDOWN.includes(fieldName) && listsSheet._lookupColumnMap?.[lookupCategory]) {
      const mapping = listsSheet._lookupColumnMap[lookupCategory];
      const formula = `Lists!$${mapping.colLetter}$2:$${mapping.colLetter}$${mapping.rowCount + 1}`;

      for (let row = 2; row <= DATA_ROWS + 1; row++) {
        ws.getCell(row, colNumber).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [formula],
          showErrorMessage: true,
          errorTitle: 'Invalid Value',
          error: `Please select a value from the ${lookupCategory} list.`
        };
      }
    }
    // Numeric range validation — whole number between min and max
    else if (NUMERIC_RANGES[fieldName]) {
      const range = NUMERIC_RANGES[fieldName];
      for (let row = 2; row <= DATA_ROWS + 1; row++) {
        ws.getCell(row, colNumber).dataValidation = {
          type: 'whole',
          operator: 'between',
          allowBlank: true,
          formulae: [range.min, range.max],
          showErrorMessage: true,
          errorTitle: 'Invalid Value',
          error: `Please enter a whole number between ${range.min} and ${range.max}.`
        };
      }
    }
    // Static enum fallback — inline list (for fields without lookup_Category)
    else if (STATIC_ENUMS[fieldName]) {
      const enumList = `"${STATIC_ENUMS[fieldName].join(',')}"`;

      for (let row = 2; row <= DATA_ROWS + 1; row++) {
        ws.getCell(row, colNumber).dataValidation = {
          type: 'list',
          allowBlank: true,
          formulae: [enumList],
          showErrorMessage: true,
          errorTitle: 'Invalid Value',
          error: `Please select from: ${STATIC_ENUMS[fieldName].join(', ')}`
        };
      }
    }

    // Date formatting — apply MM/DD/YYYY format to date columns
    if (f.uI_Type === 'date') {
      for (let row = 2; row <= DATA_ROWS + 1; row++) {
        ws.getCell(row, colNumber).numFmt = 'MM/DD/YYYY';
      }
    }

    // Time formatting — apply h:mm AM/PM format to time fields
    if (fieldName.toLowerCase().includes('time') && f.uI_Type === 'text') {
      for (let row = 2; row <= DATA_ROWS + 1; row++) {
        ws.getCell(row, colNumber).numFmt = 'h:mm AM/PM';
      }
    }
  });

  return ws;
}

// ===== Helpers =====

function getColumnLetter(colNumber) {
  let letter = '';
  let num = colNumber;
  while (num > 0) {
    const rem = (num - 1) % 26;
    letter = String.fromCharCode(65 + rem) + letter;
    num = Math.floor((num - 1) / 26);
  }
  return letter;
}
